/**
 * Asset Dictionary Cache
 * - Full HK/A stock dictionary persisted locally for offline search
 * - Source: AKTools endpoints
 *
 * Storage strategy:
 * - Use localStorage when available (browser/Tauri webview).
 * - Split HK/A lists and keep compact payloads.
 */
import type { AssetSearchResult } from '@/types/api';
import { aktoolsService } from './aktools';

type HkRow = { c: string; n: string }; // code(5 digits), name
type ARow = { c: string; n: string; e?: string }; // code(6 digits), name, exchange
type AnyRow = Record<string, any>;

export type DictMeta = {
  version: 1;
  fetchedAt: string; // ISO
  sourceBaseUrl: string;
  counts: { hk: number; a: number };
};

const LS_META = 'assetDict.meta.v1';
const LS_HK = 'assetDict.hk.v1';
const LS_A = 'assetDict.a.v1';

const DEFAULT_REFRESH_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const HK_LIST_ENDPOINTS = [
  '/api/public/stock_hk_spot_em',
  '/api/public/stock_hk_spot',
  '/api/public/stock_hk_spot_sina'
];
const A_LIST_ENDPOINTS = [
  '/api/public/stock_zh_a_spot_em',
  '/api/public/stock_zh_a_spot',
  '/api/public/stock_zh_a_spot_sina'
];

async function fetchListWithFallback(paths: string[], label: string): Promise<AnyRow[]> {
  let lastError: unknown = null;
  for (const path of paths) {
    try {
      const { data } = await aktoolsService.getJson<AnyRow[]>(path, undefined, { timeout: 60000 });
      if (Array.isArray(data) && data.length > 0) {
        console.log(`[AssetDict] ${label} list from ${path} (${data.length} rows)`);
        return data;
      }
      console.warn(`[AssetDict] ${label} list empty from ${path}`);
    } catch (error) {
      lastError = error;
      const msg = error instanceof Error ? error.message : String(error);
      console.warn(`[AssetDict] ${label} list fetch failed from ${path}: ${msg}`);
    }
  }
  if (lastError) throw lastError;
  return [];
}

function hasLocalStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

function safeJsonParse<T>(s: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function inferAExchange(code: string): 'SH' | 'SZ' | 'BJ' | undefined {
  const c = String(code || '').trim();
  if (/^6\d{5}$/.test(c)) return 'SH';
  if (/^(0|3)\d{5}$/.test(c)) return 'SZ';
  if (/^(8|9)\d{5}$/.test(c)) return 'BJ';
  return undefined;
}

function hkDisplay(code5: string): string {
  const trimmed = String(code5 || '').trim();
  const n = parseInt(trimmed, 10);
  if (Number.isFinite(n)) return String(n);
  const stripped = trimmed.replace(/^0+/, '');
  return stripped || trimmed;
}

function hkSymbolFromCode(code5: string): string {
  return `${hkDisplay(code5)}.HK`;
}

function aSymbolFromCode(code6: string, exchange?: string): { symbol: string; exchange?: string } {
  const ex = exchange || inferAExchange(code6);
  return ex ? { symbol: `${code6}.${ex}`, exchange: ex } : { symbol: code6 };
}

function extractCodeName(row: AnyRow): { code: string; name: string } {
  const codeCandidates = [
    row?.['代码'],
    row?.['code'],
    row?.['symbol'],
    row?.['股票代码'],
    row?.['证券代码']
  ];
  const nameCandidates = [
    row?.['名称'],
    row?.['name'],
    row?.['股票名称'],
    row?.['证券简称'],
    row?.['证券名称']
  ];

  const code = String(codeCandidates.find((v) => v !== undefined && v !== null) ?? '').trim();
  const name = String(nameCandidates.find((v) => v !== undefined && v !== null) ?? '').trim();
  return { code, name };
}

function normalizeFromAktoolsHk(rows: AnyRow[]): HkRow[] {
  return (Array.isArray(rows) ? rows : [])
    .map((r) => {
      const { code, name } = extractCodeName(r);
      const digits = code.replace(/\D/g, '');
      const padded = digits.padStart(5, '0');
      return { c: padded, n: name };
    })
    .filter((r) => /^\d{5}$/.test(r.c) && r.n);
}

function normalizeFromAktoolsA(rows: AnyRow[]): ARow[] {
  return (Array.isArray(rows) ? rows : [])
    .map((r) => {
      const { code, name } = extractCodeName(r);
      const digits = code.replace(/\D/g, '');
      const e = inferAExchange(digits);
      return { c: digits, n: name, ...(e ? { e } : {}) };
    })
    .filter((r) => /^\d{6}$/.test(r.c) && r.n);
}

/**
 * Check if a name indicates a bond instrument
 */
function isBondName(name: string): boolean {
  const n = String(name || '').trim();
  if (!n) return false;

  // Keywords that indicate bonds (case-insensitive for Latin keywords)
  const upper = n.toUpperCase();
  const bondKeywords = [
    '债', '债券', '债务', '中期票据', '公司债', '企业债', '国债', '可转债', '可转换债',
    '金融债', '次级债', '永续债', '超短期融资券', '短期融资券', '定向工具', '资产支持证券',
    'MTN', 'BOND', 'ABS', 'MBS'
  ];

  if (bondKeywords.some((keyword) => (keyword.length === 1 ? n.includes(keyword) : upper.includes(keyword)))) {
    return true;
  }

  // Match "BT" as a standalone token (e.g. "XIAOMI BT N3004")
  if (/\bBT\b/.test(upper)) return true;

  return false;
}

function toSearchResults(hk: HkRow[], a: ARow[]): AssetSearchResult[] {
  const results: AssetSearchResult[] = [];
  const seen = new Set<string>();

  const add = (r: AssetSearchResult) => {
    const key = `${r.symbol.toUpperCase()}-${r.type}`;
    if (seen.has(key)) return;
    seen.add(key);
    results.push(r);
  };

  for (const r of hk) {
    const symbol = hkSymbolFromCode(r.c);
    const assetType = isBondName(r.n) ? 'bond' : 'stock';
    add({
      id: `${assetType}-${symbol.toLowerCase()}`,
      symbol,
      name: r.n,
      type: assetType,
      exchange: 'HK'
    });
  }

  for (const r of a) {
    const mapped = aSymbolFromCode(r.c, r.e);
    const assetType = isBondName(r.n) ? 'bond' : 'stock';
    add({
      id: `${assetType}-${mapped.symbol.toLowerCase()}`,
      symbol: mapped.symbol,
      name: r.n,
      type: assetType,
      ...(mapped.exchange ? { exchange: mapped.exchange } : {})
    });
  }

  return results;
}

export class AssetDictionaryCache {
  private builtInCache: { hk: HkRow[]; a: ARow[] } | null = null;

  /**
   * Load built-in dictionary from JSON file (fast, synchronous after initial load)
   */
  async loadBuiltIn(): Promise<{ hk: HkRow[]; a: ARow[] }> {
    if (this.builtInCache) {
      return this.builtInCache;
    }

    try {
      // Using dynamic import for JSON in Vite
      const data = (await import('../data/builtInDictionary.json')).default as {
        version: string;
        lastUpdated: string;
        hk: HkRow[];
        a: ARow[];
      };
      this.builtInCache = { hk: data.hk || [], a: data.a || [] };
      console.log(`[AssetDict] Loaded built-in dictionary: hk=${this.builtInCache.hk.length} a=${this.builtInCache.a.length}`);
      return this.builtInCache;
    } catch (error) {
      console.warn('[AssetDict] Built-in dictionary not found or invalid, using empty:', error);
      this.builtInCache = { hk: [], a: [] };
      return this.builtInCache;
    }
  }

  /**
   * Load cached dictionary from localStorage and return AssetSearchResult[]
   * Priority: Built-in > localStorage
   */
  async loadCache(): Promise<{ meta: DictMeta | null; assets: AssetSearchResult[]; source: 'builtin' | 'localStorage' | 'none' }> {
    // Priority 1: Try built-in dictionary (fastest)
    try {
      const builtIn = await this.loadBuiltIn();
      if (builtIn.hk.length > 0 || builtIn.a.length > 0) {
        const assets = toSearchResults(builtIn.hk, builtIn.a);
        const meta: DictMeta = {
          version: 1,
          fetchedAt: new Date().toISOString(), // Use current time as "fetchedAt" for built-in
          sourceBaseUrl: 'built-in',
          counts: { hk: builtIn.hk.length, a: builtIn.a.length }
        };
        console.log(`[AssetDict] Using built-in dictionary: ${assets.length} assets`);
        return { meta, assets, source: 'builtin' };
      }
    } catch (error) {
      console.warn('[AssetDict] Failed to load built-in dictionary:', error);
    }

    // Priority 2: Fallback to localStorage
    if (!hasLocalStorage()) {
      return { meta: null, assets: [], source: 'none' };
    }
    const meta = safeJsonParse<DictMeta>(window.localStorage.getItem(LS_META));
    const hk = safeJsonParse<HkRow[]>(window.localStorage.getItem(LS_HK)) || [];
    const a = safeJsonParse<ARow[]>(window.localStorage.getItem(LS_A)) || [];
    const assets = toSearchResults(hk, a);
    if (assets.length > 0) {
      console.log(`[AssetDict] Using localStorage cache: ${assets.length} assets`);
      return { meta, assets, source: 'localStorage' };
    }

    return { meta: null, assets: [], source: 'none' };
  }

  /**
   * Decide whether we should refresh from network.
   */
  shouldRefresh(meta: DictMeta | null, ttlMs: number = DEFAULT_REFRESH_TTL_MS): boolean {
    if (!meta?.fetchedAt) return true;
    const t = new Date(meta.fetchedAt).getTime();
    if (!Number.isFinite(t)) return true;
    return Date.now() - t > ttlMs;
  }

  /**
   * Fetch HK/A lists from AKTools and persist to localStorage (if available).
   */
  async refreshFromAktoolsAndPersist(): Promise<{ meta: DictMeta; assets: AssetSearchResult[] }> {
    const baseUrl = aktoolsService.getBaseUrl();
    console.log(`[AssetDict] Starting refresh from ${baseUrl}...`);

    // Use Promise.allSettled to allow partial success (HK or A can fail independently)
    const [hkSettled, aSettled] = await Promise.allSettled([
      fetchListWithFallback(HK_LIST_ENDPOINTS, 'HK'),
      fetchListWithFallback(A_LIST_ENDPOINTS, 'A-share')
    ]);

    let hk: HkRow[] = [];
    let a: ARow[] = [];
    let hasAnySuccess = false;

    // Process HK result
    if (hkSettled.status === 'fulfilled') {
      try {
        hk = normalizeFromAktoolsHk(hkSettled.value);
        hasAnySuccess = true;
        console.log(`[AssetDict] HK list fetched: ${hk.length} items`);
      } catch (error) {
        console.warn(`[AssetDict] Failed to normalize HK data:`, error);
      }
    } else {
      const error = hkSettled.reason;
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error(`[AssetDict] HK list fetch failed: ${errorMsg}`);
    }

    // Process A-share result
    if (aSettled.status === 'fulfilled') {
      try {
        a = normalizeFromAktoolsA(aSettled.value);
        hasAnySuccess = true;
        console.log(`[AssetDict] A-share list fetched: ${a.length} items`);
      } catch (error) {
        console.warn(`[AssetDict] Failed to normalize A-share data:`, error);
      }
    } else {
      const error = aSettled.reason;
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error(`[AssetDict] A-share list fetch failed: ${errorMsg}`);
    }

    // If both failed, throw error
    if (!hasAnySuccess) {
      const hkError = hkSettled.status === 'rejected' ? hkSettled.reason : null;
      const aError = aSettled.status === 'rejected' ? aSettled.reason : null;
      const combinedError = new Error(
        `Both HK and A-share fetches failed. HK: ${hkError instanceof Error ? hkError.message : String(hkError)}; A: ${aError instanceof Error ? aError.message : String(aError)}`
      );
      console.error(`[AssetDict] Refresh failed completely:`, combinedError);
      throw combinedError;
    }

    // Save partial or full results
    const meta: DictMeta = {
      version: 1,
      fetchedAt: new Date().toISOString(),
      sourceBaseUrl: baseUrl,
      counts: { hk: hk.length, a: a.length }
    };

    if (hasLocalStorage()) {
      // Persist compact payloads (even if partial)
      window.localStorage.setItem(LS_META, JSON.stringify(meta));
      window.localStorage.setItem(LS_HK, JSON.stringify(hk));
      window.localStorage.setItem(LS_A, JSON.stringify(a));
    }

    const assets = toSearchResults(hk, a);
    const statusMsg = hk.length > 0 && a.length > 0 
      ? 'full refresh' 
      : hk.length > 0 
        ? 'partial refresh (HK only)' 
        : 'partial refresh (A-share only)';
    console.log(`[AssetDict] Refresh ${statusMsg}: hk=${hk.length} a=${a.length} total=${assets.length}`);
    return { meta, assets };
  }
}

export const assetDictionaryCache = new AssetDictionaryCache();

